// Signage → Avisos del local (/signage/avisos). Autonomía del local (419): el
// gerente/encargado (asignado a una caja de la sucursal — no necesita nivel 5)
// publica avisos rápidos al ticker de las pantallas de SU local (propagan en
// ≤20 s), los cierra, y ve/opera sus pantallas (refrescar, captura si el box
// corre Fully Kiosk). El permiso real lo valida el servidor por local.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Megaphone, RefreshCcw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Campo, Modal, fieldCls } from '@/features/pos/shared';
import { SIG, sigRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Panel {
  locales: { id: string; nombre: string }[];
  avisos: { id: string; local: string; location_id: string; texto: string; desde: string; hasta: string; vigente: boolean }[];
  pantallas: { id: string; nombre: string; location_id: string; local: string; online: boolean; last_seen: string | null; salud: { kiosk?: boolean; bateria?: number } | null }[];
}

const HORAS = [{ v: 1, l: '1 hora' }, { v: 2, l: '2 horas' }, { v: 4, l: '4 horas' }, { v: 8, l: 'Todo el día (8 h)' }, { v: 72, l: '3 días' }];

export function AvisosLocal() {
  const qc = useQueryClient();
  const { data: panel } = useQuery<Panel>({
    queryKey: ['sig-avisos-panel'], staleTime: 0, refetchInterval: 30000,
    queryFn: () => sigRpc<Panel>('sig_avisos_panel'),
  });
  const [loc, setLoc] = useState('');
  const [texto, setTexto] = useState('');
  const [horas, setHoras] = useState(4);
  const locales = panel?.locales ?? [];
  const locSel = loc || locales[0]?.id || '';

  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-avisos-panel'] });

  const publicar = useMutation({
    mutationFn: () => sigRpc('sig_aviso_publicar', { _location: locSel, _texto: texto.trim(), _horas: horas }),
    onSuccess: () => { toast.success('Aviso publicado — sale en las pantallas en menos de 20 segundos'); setTexto(''); void invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: (id: string) => sigRpc('sig_aviso_cerrar', { _id: id }),
    onSuccess: () => { toast.success('Aviso retirado'); void invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const refrescar = async (pid: string, nombre: string) => {
    try { await apiClient.post('/rpc/sig_comando_enviar', { _pantalla: pid, _tipo: 'refrescar' }, SIG); toast.success(`Refrescar enviado a ${nombre}`); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  // Captura remota (Fully Kiosk): mismo circuito de comandos con polling
  const [captura, setCaptura] = useState<{ pantalla: string; img?: string; msg?: string; esperando: boolean } | null>(null);
  const capturar = async (pid: string, nombre: string) => {
    setCaptura({ pantalla: nombre, esperando: true });
    try {
      const r = (await apiClient.post('/rpc/sig_comando_enviar', { _pantalla: pid, _tipo: 'captura' }, SIG)).data as { id: string };
      const t0 = Date.now();
      for (;;) {
        if (Date.now() - t0 > 60000) { setCaptura({ pantalla: nombre, msg: 'La pantalla no respondió en 60 s', esperando: false }); return; }
        await new Promise((res) => setTimeout(res, 3000));
        const e = (await apiClient.post('/rpc/sig_comando_estado', { _id: r.id }, SIG)).data as { estado: string; resultado?: { img?: string; msg?: string } };
        if (e.estado === 'hecho') { setCaptura({ pantalla: nombre, img: e.resultado?.img, msg: e.resultado?.img ? undefined : 'Respondió sin imagen', esperando: false }); return; }
        if (e.estado === 'error') { setCaptura({ pantalla: nombre, msg: e.resultado?.msg ?? 'El box reportó un error', esperando: false }); return; }
      }
    } catch (e) { setCaptura(null); toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Avisos del local</h1>
      <p className="text-sm text-muted-foreground">
        Publica un aviso corto y sale en el <b>ticker</b> de las pantallas de tu local en menos de 20 segundos
        («Hoy 2x1 en helados hasta las 5», «Caja 2 cerrada»). Solo puedes publicar en los locales donde estás asignado a una caja.
      </p>

      {!locales.length && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          No estás asignado a ninguna caja. Pide al administrador que te asigne a una caja de tu sucursal (POS → Cajas).
        </div>
      )}

      {locales.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Nuevo aviso</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_10rem_10rem] gap-2 items-end">
              <Campo label={`Texto (máx. 140) — ${140 - texto.length} restantes`}>
                <input className={fieldCls} maxLength={140} value={texto} placeholder="⚡ Hoy 2x1 en helados hasta las 17h"
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && texto.trim().length >= 3) publicar.mutate(); }} />
              </Campo>
              <Campo label="Local">
                <select className={fieldCls} value={locSel} onChange={(e) => setLoc(e.target.value)}>
                  {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Vigencia">
                <select className={fieldCls} value={horas} onChange={(e) => setHoras(Number(e.target.value))}>
                  {HORAS.map((h) => <option key={h.v} value={h.v}>{h.l}</option>)}
                </select>
              </Campo>
            </div>
            <div className="flex justify-end">
              <Button isLoading={publicar.isPending} disabled={texto.trim().length < 3} onClick={() => publicar.mutate()}>Publicar al ticker</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {(panel?.avisos ?? []).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Avisos de los últimos 7 días</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(panel?.avisos ?? []).map((a) => (
              <div key={a.id} className={`flex items-center gap-2 text-sm border-b last:border-0 pb-1.5 ${a.vigente ? '' : 'opacity-50'}`}>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.vigente ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-muted text-muted-foreground'}`}>
                  {a.vigente ? 'AL AIRE' : 'Vencido'}
                </span>
                <span className="flex-1">{a.texto}</span>
                <span className="text-xs text-muted-foreground">{a.local} · hasta {new Date(a.hasta).toLocaleString(LOCALE, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                {a.vigente && (
                  <button className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" onClick={() => cerrar.mutate(a.id)}>
                    <XCircle className="w-3.5 h-3.5" /> Retirar
                  </button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(panel?.pantallas ?? []).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Pantallas de mi(s) local(es)</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {(panel?.pantallas ?? []).map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm border-b last:border-0 pb-1.5">
                <span className={`w-2 h-2 rounded-full ${p.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                <span className="flex-1">{p.nombre} <span className="text-xs text-muted-foreground">· {p.local}</span></span>
                {p.salud?.kiosk && <span className="rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1 text-[11px] font-medium">Fully</span>}
                {p.salud?.bateria != null && <span className="text-xs text-muted-foreground">{p.salud.bateria}%</span>}
                <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Refrescar contenido" onClick={() => void refrescar(p.id, p.nombre)}><RefreshCcw className="w-4 h-4" /></button>
                <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Capturar pantalla (Fully Kiosk)" onClick={() => void capturar(p.id, p.nombre)}><Camera className="w-4 h-4" /></button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {captura && (
        <Modal title={`Captura — ${captura.pantalla}`} onClose={() => setCaptura(null)}>
          <div className="space-y-3">
            {captura.esperando && <p className="text-sm text-muted-foreground py-6 text-center">Esperando al box… (~15-25 s)</p>}
            {captura.img && <img src={captura.img} alt="Captura de la pantalla" className="w-full rounded-md border" />}
            {captura.msg && <p className="text-sm text-amber-700 dark:text-amber-300">{captura.msg}</p>}
            <div className="flex justify-end"><Button variant="ghost" onClick={() => setCaptura(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
